import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Mail, Building2, MessageSquareText, Filter, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

const TYPE_LABEL = {
  partnership: "B2B Partnership",
  hospitality: "Luxury Hospitality",
  retail: "Premium Retail",
  press: "Press & Media",
  other: "Other",
};

const TYPE_COLOR = {
  partnership: "#2C3E35",
  hospitality: "#B45309",
  retail: "#1E40AF",
  press: "#7E22CE",
  other: "#525252",
};

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const InquiriesManager = ({ onUnauthorized }) => {
  const [inquiries, setInquiries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [deleting, setDeleting] = useState("");
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/inquiries/export`, {
        headers: auth(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nyla-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const load = () => {
    axios
      .get(`${API}/admin/inquiries`, { headers: auth() })
      .then((r) => setInquiries(r.data))
      .catch((e) => {
        if (e.response?.status === 401) onUnauthorized();
        else toast.error("Could not load inquiries");
      });
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: auth() });
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Inquiry removed");
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error("Failed to delete");
    } finally {
      setDeleting("");
    }
  };

  if (!inquiries) return <p className="text-sm text-[#86868B]">Loading…</p>;

  const filtered = filter === "all" ? inquiries : inquiries.filter((i) => i.inquiry_type === filter);
  const typeCounts = inquiries.reduce((acc, i) => {
    acc[i.inquiry_type] = (acc[i.inquiry_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="inquiries-manager">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Leads &amp; Inquiries</h1>
          <p className="text-sm text-[#86868B]">
            Every submission from the Contact form — sorted newest first.
            {inquiries.length > 0 && (
              <span className="ml-2 text-[#1D1D1F] font-medium">
                {filtered.length} of {inquiries.length}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="inquiries-export-csv"
            onClick={exportCsv}
            disabled={exporting || inquiries.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2C3E35] text-white text-xs font-medium hover:bg-[#223129] disabled:opacity-50 transition-colors duration-200"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export CSV
          </button>
          <div className="flex items-center gap-1.5 text-xs text-[#86868B]">
            <Filter className="w-3.5 h-3.5" />
            Filter:
          </div>
          {["all", "partnership", "hospitality", "retail", "press", "other"].map((t) => (
            <button
              key={t}
              data-testid={`inquiries-filter-${t}`}
              onClick={() => setFilter(t)}
              className={`px-3.5 py-1.5 rounded-full text-[11px] font-medium transition-colors duration-200 ${
                filter === t
                  ? "bg-[#1D1D1F] text-white"
                  : "bg-white text-[#1D1D1F]/70 hover:bg-black/5 border border-[#D2D2D7]/60"
              }`}
            >
              {t === "all" ? `All (${inquiries.length})` : `${TYPE_LABEL[t]} (${typeCounts[t] || 0})`}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <MessageSquareText className="w-8 h-8 text-[#86868B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#86868B]">No inquiries match this filter yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((i) => (
            <div
              key={i.id}
              data-testid={`inquiry-row-${i.id}`}
              className="bg-white rounded-3xl p-5 md:p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] transition-shadow duration-200"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <p className="font-display text-lg font-bold text-[#1D1D1F] truncate">{i.name}</p>
                    <span
                      className="text-[10px] tracking-[0.15em] uppercase font-semibold px-2.5 py-0.5 rounded-full"
                      style={{ backgroundColor: `${TYPE_COLOR[i.inquiry_type] || "#525252"}15`, color: TYPE_COLOR[i.inquiry_type] || "#525252" }}
                    >
                      {TYPE_LABEL[i.inquiry_type] || i.inquiry_type}
                    </span>
                    <span className="text-[11px] text-[#86868B]">{fmt(i.created_at)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-[#86868B] mb-3">
                    <a
                      href={`mailto:${i.email}`}
                      data-testid={`inquiry-email-${i.id}`}
                      className="flex items-center gap-1.5 hover:text-[#2C3E35] transition-colors duration-200"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {i.email}
                    </a>
                    {i.company && (
                      <span className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5" />
                        {i.company}
                      </span>
                    )}
                  </div>
                  <p
                    className={`text-sm text-[#1D1D1F]/80 leading-relaxed whitespace-pre-wrap ${
                      selected?.id === i.id ? "" : "line-clamp-2"
                    }`}
                  >
                    {i.message}
                  </p>
                  {i.message && i.message.length > 160 && (
                    <button
                      data-testid={`inquiry-toggle-${i.id}`}
                      onClick={() => setSelected(selected?.id === i.id ? null : i)}
                      className="mt-2 text-xs font-medium text-[#2C3E35] hover:underline"
                    >
                      {selected?.id === i.id ? "Show less" : "Show full message"}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href={`mailto:${i.email}?subject=Re:%20Your%20Nyla%20Inquiry`}
                    data-testid={`inquiry-reply-${i.id}`}
                    className="px-4 py-2 rounded-full bg-[#2C3E35] text-white text-xs font-medium hover:bg-[#223129] transition-colors duration-200"
                  >
                    Reply
                  </a>
                  <button
                    data-testid={`inquiry-delete-${i.id}`}
                    onClick={() => remove(i.id)}
                    disabled={deleting === i.id}
                    aria-label="Delete inquiry"
                    className="p-2 rounded-full text-[#86868B] hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors duration-200"
                  >
                    {deleting === i.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
